import { useCallback, useEffect, useState } from 'react';
import { commentService } from '../services/api/comments';
import { CommentEntry, SaveCommentPayload } from '../types/comments';

export const useComments = (
  moldOrderId: number,
  authorId?: number,
  enabled: boolean = true,
) => {
  const [comments, setComments] = useState<CommentEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (!enabled) {
      setComments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await commentService.list(moldOrderId);
      setComments(response.data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      setError('โหลดความคิดเห็นไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [enabled, moldOrderId]);

  useEffect(() => {
    void fetchComments();
  }, [fetchComments]);

  const createComment = useCallback(
    async (payload: SaveCommentPayload) => {
      if (!authorId) {
        throw new Error('Missing current user id');
      }

      setSubmitting(true);
      setError(null);
      try {
        await commentService.create(moldOrderId, authorId, payload);
        await fetchComments();
      } catch (err) {
        console.error('Failed to create comment:', err);
        setError('บันทึกความคิดเห็นไม่สำเร็จ');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [authorId, fetchComments, moldOrderId],
  );

  const updateComment = useCallback(
    async (commentId: number, payload: SaveCommentPayload) => {
      setSubmitting(true);
      setError(null);
      try {
        await commentService.update(moldOrderId, commentId, payload);
        await fetchComments();
      } catch (err) {
        console.error('Failed to update comment:', err);
        setError('แก้ไขความคิดเห็นไม่สำเร็จ');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [fetchComments, moldOrderId],
  );

  const deleteComment = useCallback(
    async (commentId: number) => {
      setSubmitting(true);
      setError(null);
      try {
        await commentService.delete(moldOrderId, commentId);
        await fetchComments();
      } catch (err) {
        console.error('Failed to delete comment:', err);
        setError('ลบความคิดเห็นไม่สำเร็จ');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [fetchComments, moldOrderId],
  );

  return {
    comments,
    loading,
    submitting,
    error,
    fetchComments,
    createComment,
    updateComment,
    deleteComment,
  };
};
